import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps } from 'formik';
import { Add } from '@material-ui/icons';
import { DeliveryListSuppliersQuery, useDeliveryListSuppliersQuery } from '../../../../../../generated/graphql';
import { useDebounce } from '../../../../../../utils';
import { AutocompleteSearch, CREATE_NEW_STRING, FETCH_MORE_STRING } from '../../../../../../components/forms/fields';

type Suppliers = DeliveryListSuppliersQuery['suppliers'];
type Supplier = Suppliers[0];

const LIMIT = 10;

interface OptionProps {
    option: Supplier;
    isLoading?: boolean;
}

const Option = ({ isLoading, option }: OptionProps) => {
    const { t } = useTranslation();
    if (option.id === FETCH_MORE_STRING) {
        return isLoading
            ? <CircularProgress size={16} />
            : <Typography>{t('Show more')}</Typography>;
    }
    if (option.id === CREATE_NEW_STRING) {
        return (
            <Box display='flex'>
                <Add color='primary' />
                <Box mr={0.5} />
                <Typography color='primary'>{t('New supplier')}</Typography>
            </Box>
        );
    }

    return <Typography>{option.legalName}</Typography>;
};

interface SupplierSearchProps {
    label: string;
    value: string | null;
    onChange(val: string | null): void;
    onBlur(event: React.FocusEvent<HTMLDivElement>): void;
    helpertext?: string;
    error?: boolean;
    disabled?: boolean;
    onClick?(): void;
    onCreateNewClick(): void;
    passedSearchValue?: string;
}

const SupplierSearch = (
    { label, onChange, value, onBlur, helpertext, error, disabled, onClick, onCreateNewClick, passedSearchValue }: SupplierSearchProps
) => {
    const [searchQuery, setSearchQuery] = useState(passedSearchValue || '');
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const { data, fetchMore, loading, refetch } = useDeliveryListSuppliersQuery({ variables: { limit: LIMIT } });
    const debouncedSearchQuery = useDebounce(searchQuery);

    const suppliers: Suppliers = data ? data.suppliers : [];

    useEffect(() => {
        if (offset === 0) {
            if ((suppliers.length === 0 || suppliers.length % LIMIT !== 0)) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        }
    }, [suppliers, offset]);

    useEffect(() => {
        setOffset(0);
        // eslint-disable-next-line no-void
        void refetch({ multiValueSearch: debouncedSearchQuery, offset: 0 });
    }, [refetch, debouncedSearchQuery]);

    const handleFetchMore = async () => {
        if (fetchMore) {
            const newOffset = offset + LIMIT;
            setIsFetchingMore(true);
            const { data: newData } = await fetchMore({ variables: { offset: newOffset } });
            setIsFetchingMore(false);
            setOffset(newOffset);

            if (newData.suppliers.length < LIMIT) {
                setHasMore(false);
            }
        }

    };

    const onSearchResultClick = (_e: React.ChangeEvent<Record<string, unknown>>, val: Supplier | null) => onChange(val ? val.id : null);

    const finalValue = suppliers.find(({ id }) => id === value) || null;

    const onInputChange = (val: string) => setSearchQuery(val);
    const getOptionLabel = (option: Supplier) => option.legalName || '';

    const isLoading = isFetchingMore || loading;

    return (
        <AutocompleteSearch
            label={label}
            showFetchMore={hasMore}
            onCreateNew={onCreateNewClick}
            margin='none'
            onFetchMore={handleFetchMore}
            error={error}
            helpertext={helpertext}
            onBlur={onBlur}
            options={suppliers}
            isLoading={isLoading}
            renderOption={(option: Supplier) => <Option option={option} isLoading={isLoading} />}
            getOptionLabel={getOptionLabel}
            value={finalValue}
            onSearchResultClick={onSearchResultClick}
            searchQuery={searchQuery}
            onInputChange={onInputChange}
            disabled={disabled}
            onClick={onClick}
        />
    );
};
interface SearchSupplierFieldProps {
    name: string;
    label: string;
    onCreateNewClick(): void;
    passedSearchValue?: string;
    openMobilePopup?(): void;
}

export const SearchSupplierField = ({ name, label, onCreateNewClick, passedSearchValue, openMobilePopup }: SearchSupplierFieldProps) => {
    return (
        <Field
            name={name}
            component={({ field, form }: FieldProps<string | null>) => {
                const { value, onChange, onBlur } = field;
                const helperText = form.touched[name] ? form.errors[name] as string : '';

                const handleChange = (val: string | null) => onChange({ target: { value: val, name } });
                return (
                    <SupplierSearch
                        onCreateNewClick={onCreateNewClick}
                        helpertext={helperText || ' '}
                        error={!!helperText}
                        label={label}
                        onBlur={onBlur}
                        value={value}
                        passedSearchValue={passedSearchValue}
                        onClick={openMobilePopup}
                        disabled={!!openMobilePopup}
                        onChange={handleChange}
                    />
                );
            }}
        />
    );
};

export default SearchSupplierField;